import PrimaryButton from "@/Components/UI/Forms/PrimaryButton";
import Modal from "@/Components/UI/Modal";
import { AnalyticsContext } from "@/Contexts/AnalyticsContext";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { FaCheck } from "react-icons/fa6";
import { FiLoader } from "react-icons/fi";
import { IoCopySharp } from "react-icons/io5";
import { RiInformationFill } from "react-icons/ri";
import { z } from "zod";
import SecondaryButton from "../Forms/SecondaryButton";
import Tooltip from "../Tooltip";

const clientGiftDataSchema = z.object({
    clientGiftUrl: z.string(),
    numberUsed: z.number(),
});

type ClientGiftData = z.infer<typeof clientGiftDataSchema>;

type GiftFreeTrialModalProps = {
    showModal: boolean;
    onRequestClose: () => void;
};

export default function GiftFreeTrialModal({
    showModal,
    onRequestClose,
}: GiftFreeTrialModalProps) {
    const [clientGiftData, setClientGiftData] = useState<ClientGiftData | null>(
        null,
    );
    const [showIsCopied, setShowIsCopied] = useState(false);

    const getClientGiftData = async () => {
        setClientGiftData(null);
        const response = await axios.get<ClientGiftData>(
            route("referral-program.referrer-data"),
        );

        setClientGiftData(clientGiftDataSchema.parse(response.data));
    };

    useEffect(() => {
        if (showModal) {
            getClientGiftData();
        }
    }, [showModal]);

    useEffect(() => {
        if (showIsCopied) {
            setTimeout(() => {
                setShowIsCopied(false);
            }, 2000);
        }
    }, [showIsCopied]);

    const analytics = useContext(AnalyticsContext);

    return (
        <Modal
            id="gift-free-trial-modal"
            onClose={() => onRequestClose()}
            show={showModal}
            maxWidth="3xl"
            showCloseButton={true}
            animateClose={false}
        >
            <div className="flex flex-col items-stretch max-w-[500px] p-8 gap-4 text-brand-black">
                <div className="mt-2 self-stretch text-xl font-bold text-center">
                    Gift a free trial
                </div>
                <div className="flex justify-center">
                    <img
                        src={`${import.meta.env.VITE_CDN_URL}/images/gift.svg`}
                        alt="Gift Free Trial"
                    />
                </div>
                <div>Know someone who could benefit from coaching?</div>
                <div>
                    You can give them something special: a free trial of
                    coaching to help them move forward!
                </div>
                <div className="text-brand-gray text-sm">
                    Note: We don't offer free trials on our website, this is
                    exclusively for existing clients to share with others.
                </div>
                <div className="flex flex-col items-center gap-2 bg-brand-lightGray rounded-lg p-4">
                    {clientGiftData ? (
                        <div className="text-5xl font-bold">
                            {Math.min(clientGiftData.numberUsed, 5)} / 5
                        </div>
                    ) : (
                        <FiLoader className="animate-spin" size={32} />
                    )}
                    <div className="flex items-center">
                        Invites used{" "}
                        <Tooltip
                            popupContainerClassName="ml-1"
                            title="Once someone starts a trial with your link, the invite count will be updated."
                            position="top"
                        >
                            <RiInformationFill className="text-xs text-brand-blueDecal md:text-base" />
                        </Tooltip>
                    </div>
                </div>

                {navigator.share !== undefined && (
                    <PrimaryButton
                        className="justify-center self-stretch gap-2"
                        onClick={() => {
                            navigator.share({
                                title: "Hey! I've been working with a coach on Strawberry.me and getting a lot out of it.\n\nI have a few free trials I can gift – here’s one for you:",
                                text: `${clientGiftData?.clientGiftUrl}`,
                            });
                            analytics.trackEvent("Referral Share Link Clicked");
                        }}
                        disabled={!clientGiftData}
                    >
                        Share Link
                    </PrimaryButton>
                )}
                <SecondaryButton
                    className="justify-center self-stretch gap-2"
                    onClick={() => {
                        navigator.clipboard.writeText(
                            clientGiftData?.clientGiftUrl || "",
                        );
                        setShowIsCopied(true);
                        analytics.trackEvent("Referral Copy Link Clicked");
                    }}
                    disabled={!clientGiftData}
                >
                    {showIsCopied ? (
                        <>
                            <FaCheck />
                            Copied!
                        </>
                    ) : (
                        <>
                            <IoCopySharp />
                            Copy Link
                        </>
                    )}
                </SecondaryButton>
            </div>
        </Modal>
    );
}
